/**
 * Inline editor for a channel-dataset's train/eval split.
 *
 * Presentational organism — owns the form's local state but is dumb
 * about persistence. The parent supplies `onSave` (typically a
 * `useSaveSplit` mutation call) and `onCancel`. Used in two places:
 *   1. Inside `ChannelDatasetDetail` (Datasets tab, expanded card)
 *   2. Inside `SplitTab` (the standalone Train / Eval Split tab)
 *
 * Live preview math + warning thresholds match the backend's logic so
 * the user sees the same warnings the server will return:
 *   evaluation = round(finalised * ratio)
 *   training   = finalised - evaluation
 *   recommendedMin = max(10, round(finalised * 0.05))
 */

import React, { useState } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

import { Button } from '../../../../../ui/button';
import { Input } from '../../../../../ui/input';
import { Label } from '../../../../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../../ui/radio-group';
import { Slider } from '../../../../../ui/slider';
import { cn } from '../../../../../../lib/utils';
import type {
  ChannelDataset,
  SaveSplitPayload,
  SplitMethod,
  SplitPreset,
} from '../../types';

interface PresetEntry {
  ratio: number;
  method: SplitMethod;
  label: string;
  description: string;
}

const SPLIT_METHODS: readonly SplitMethod[] = ['stratified', 'random'] as const;
const SPLIT_PRESETS: readonly SplitPreset[] = [
  'balanced',
  'conservative',
  'maxeval',
  'custom',
] as const;

const isSplitPreset = (v: string): v is SplitPreset =>
  (SPLIT_PRESETS as readonly string[]).includes(v);

const isSplitMethod = (v: string): v is SplitMethod =>
  (SPLIT_METHODS as readonly string[]).includes(v);

const PRESETS: Record<Exclude<SplitPreset, 'custom'>, PresetEntry> = {
  balanced: {
    ratio: 0.8,
    method: 'stratified',
    label: 'Balanced (80/20)',
    description: 'default; recommended',
  },
  conservative: {
    ratio: 0.7,
    method: 'stratified',
    label: 'Conservative (70/30)',
    description: 'fewer eval records but more few-shot training examples',
  },
  maxeval: {
    ratio: 0.9,
    method: 'stratified',
    label: 'Max evaluation (90/10)',
    description: 'maximises eval coverage; fewer few-shot examples',
  },
};

const METHOD_OPTIONS: Array<{ value: SplitMethod; label: string; description: string }> = [
  {
    value: 'stratified',
    label: 'Stratified',
    description: 'Recommended — keeps label distribution proportional between sets.',
  },
  {
    value: 'random',
    label: 'Random',
    description: 'Distributes records uniformly without preserving label balance.',
  },
];

interface Props {
  /** Channel dataset being edited. `stats.finalised` drives the preview math. */
  dataset: ChannelDataset;
  /** Called with the canonical payload when the user clicks Save. */
  onSave: (payload: SaveSplitPayload) => void;
  onCancel: () => void;
  /** When true, disables Save while the mutation is in-flight. */
  isPending?: boolean;
}

const SplitEditor: React.FC<Props> = ({ dataset, onSave, onCancel, isPending = false }) => {
  const finalised = dataset.stats.finalised;
  const initial = dataset.split;

  // `initial?.preset` / `initial?.method` are already typed as the
  // closed unions (Split.preset / Split.method on types.ts), so no
  // cast needed at the read site.
  const [preset, setPreset] = useState<SplitPreset>(initial?.preset ?? 'balanced');
  const [ratio, setRatio] = useState<number>(
    initial?.ratio ?? PRESETS.balanced.ratio,
  );
  const [method, setMethod] = useState<SplitMethod>(
    initial?.method ?? PRESETS.balanced.method,
  );
  // Advanced: random-seed input. Defaults to the existing split's seed
  // when re-splitting, blank otherwise (the BE applies its own default
  // when the field is absent).
  const [seedInput, setSeedInput] = useState<string>(
    initial?.seed != null ? String(initial.seed) : '',
  );
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);

  const evaluation = Math.round(finalised * ratio);
  const training = finalised - evaluation;
  const recommendedMin = Math.max(10, Math.round(finalised * 0.05));

  // Count-based warnings are suppressed while `finalised === 0` because
  // pre-B1 `dataset.stats.finalised` is zero-stubbed for every row (the
  // BE's `_aggregate_channel_stats` returns 0 until `dataset_records`
  // exists). With zero records, `training` is always 0 and the
  // hard-warning would fire on every editor open — noise, not a real
  // signal. Once B1 ships and `finalised` carries a real count, drop
  // the `finalised > 0` guard to re-enable the warnings.
  // TODO(B1): remove the `finalised > 0` precondition.
  const showHardWarning = finalised > 0 && training < 10;
  const showSoftWarning =
    finalised > 0 && !showHardWarning && training < recommendedMin;
  // Only warn when re-splitting will actually shift record assignments.
  // If `(seed, method, ratio)` match the persisted split, the partition
  // is identical (deterministic given the same RNG inputs), so prompts
  // don't need to change. The "new records uploaded since last split"
  // case will warrant the warning too, but that nuance is post-B1 —
  // pre-B1 there are no `dataset_records` to count.
  const seedChanged = (() => {
    if (!initial) return false;
    const trimmed = seedInput.trim();
    if (trimmed === '') return false; // omit → BE default; no shift on its own
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed !== initial.seed;
  })();
  const showResplitWarning =
    initial !== null &&
    (seedChanged || method !== initial.method || ratio !== initial.ratio);

  const handlePresetChange = (next: string) => {
    if (!isSplitPreset(next)) return;
    setPreset(next);
    if (next !== 'custom') {
      setRatio(PRESETS[next].ratio);
      setMethod(PRESETS[next].method);
    }
  };

  const handleSliderChange = (values: number[]) => {
    const next = values[0] ?? 0.8;
    setRatio(next);
    setPreset('custom');
  };

  const handleMethodChange = (next: string) => {
    if (!isSplitMethod(next)) return;
    setMethod(next);
  };

  const handleSave = () => {
    // `seed`: parse the input only if the user supplied a value;
    // otherwise omit so the BE applies its own default.
    const trimmed = seedInput.trim();
    const parsedSeed = trimmed === '' ? undefined : Number.parseInt(trimmed, 10);
    const seed =
      parsedSeed !== undefined && Number.isFinite(parsedSeed) && parsedSeed >= 0
        ? parsedSeed
        : undefined;
    onSave({
      preset,
      ratio,
      method,
      finalised,
      ...(seed !== undefined ? { seed } : {}),
    });
  };

  const evalPct = Math.round(ratio * 100);
  const trainPct = 100 - evalPct;

  return (
    <div
      className="space-y-5 rounded-md border border-border bg-card p-4"
      data-testid="split-editor"
    >
      {/* Preset radio group */}
      <div className="space-y-2">
        <Label className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          Preset
        </Label>
        <RadioGroup
          value={preset}
          onValueChange={handlePresetChange}
          className="grid gap-2 md:grid-cols-2"
        >
          {(Object.keys(PRESETS) as Array<keyof typeof PRESETS>).map((key) => (
            <label
              key={key}
              htmlFor={`preset-${key}`}
              className={cn(
                'flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors',
                preset === key
                  ? 'border-primary/40 bg-accent/40'
                  : 'border-border hover:bg-muted/30',
              )}
            >
              <RadioGroupItem value={key} id={`preset-${key}`} className="mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-foreground">
                  {PRESETS[key].label}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {PRESETS[key].description}
                </div>
              </div>
            </label>
          ))}
          <label
            htmlFor="preset-custom"
            className={cn(
              'flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors md:col-span-2',
              preset === 'custom'
                ? 'border-primary/40 bg-accent/40'
                : 'border-border hover:bg-muted/30',
            )}
          >
            <RadioGroupItem value="custom" id="preset-custom" className="mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-foreground">Custom</div>
              <div className="text-[11px] text-muted-foreground">
                User enters their own ratio.
              </div>
            </div>
          </label>
        </RadioGroup>
      </div>

      {/* Slider */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
            Evaluation ratio
          </Label>
          <span
            className="text-sm font-semibold tabular-nums text-foreground"
            data-testid="ratio-display"
          >
            {evalPct}%
          </span>
        </div>
        <Slider
          value={[ratio]}
          onValueChange={handleSliderChange}
          min={0.5}
          max={0.99}
          step={0.05}
          aria-label="Evaluation ratio"
          data-testid="ratio-slider"
        />
        <div className="flex justify-between text-[11px] text-muted-foreground">
          <span>50%</span>
          <span>99%</span>
        </div>
      </div>

      {/* Method radio group */}
      <div className="space-y-2">
        <Label className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          Method
        </Label>
        <RadioGroup
          value={method}
          onValueChange={handleMethodChange}
          className="grid gap-2 md:grid-cols-2"
        >
          {METHOD_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              htmlFor={`method-${opt.value}`}
              className={cn(
                'flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors',
                method === opt.value
                  ? 'border-primary/40 bg-accent/40'
                  : 'border-border hover:bg-muted/30',
              )}
            >
              <RadioGroupItem
                value={opt.value}
                id={`method-${opt.value}`}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-foreground">
                  {opt.label}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {opt.description}
                </div>
              </div>
            </label>
          ))}
        </RadioGroup>
      </div>

      {/* Live counts */}
      <div
        className="grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/20 p-3"
        data-testid="split-preview"
      >
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Training ({trainPct}%)
          </div>
          <div className="mt-1 text-lg font-bold tabular-nums text-primary">
            {training.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Evaluation ({evalPct}%)
          </div>
          <div className="mt-1 text-lg font-bold tabular-nums text-emerald-600">
            {evaluation.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Warnings */}
      <div className="space-y-2">
        {showHardWarning && (
          <WarningBlock tone="error" testid="warning-below-ten">
            Training set will have fewer than 10 records. This is below the
            absolute minimum and may cause classification failures.
          </WarningBlock>
        )}
        {showSoftWarning && (
          <WarningBlock tone="warning" testid="warning-below-recommended">
            Training set would have {training.toLocaleString()} records (minimum
            recommended: {recommendedMin.toLocaleString()}). Some labels may
            have zero few-shot examples.
          </WarningBlock>
        )}
        {showResplitWarning && (
          <WarningBlock tone="info" testid="warning-resplit">
            Re-splitting will change which records land in each set. Prompts
            using the current training set may need updating.
          </WarningBlock>
        )}
      </div>

      {/* Advanced: random seed. Hidden by default; users who care about
          reproducibility can pin a specific seed. Empty value → BE
          applies its own default. */}
      <div className="space-y-2">
        <button
          type="button"
          className="text-[12px] font-medium text-muted-foreground hover:text-foreground cursor-pointer"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
          aria-controls="split-editor-advanced"
          data-testid="split-advanced-toggle"
        >
          {advancedOpen ? '− Hide advanced' : '+ Show advanced'}
        </button>
        {advancedOpen && (
          <div
            id="split-editor-advanced"
            className="space-y-1"
            data-testid="split-editor-advanced"
          >
            <Label htmlFor="split-seed-input" className="text-[12px]">
              Random seed (optional)
            </Label>
            <Input
              id="split-seed-input"
              type="number"
              inputMode="numeric"
              min={0}
              value={seedInput}
              onChange={(e) => setSeedInput(e.target.value)}
              className="w-24"
              data-testid="split-seed-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Leave blank for default. Controls which records land in training
              vs evaluation — use the same seed to reproduce an earlier split.
            </p>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={isPending}>
          {isPending ? 'Saving…' : 'Save split'}
        </Button>
      </div>
    </div>
  );
};

interface WarningBlockProps {
  tone: 'error' | 'warning' | 'info';
  testid: string;
  children: React.ReactNode;
}

const WarningBlock: React.FC<WarningBlockProps> = ({ tone, testid, children }) => {
  const toneClass =
    tone === 'error'
      ? 'border-l-red-600 bg-red-50 text-red-900'
      : tone === 'warning'
      ? 'border-l-amber-600 bg-amber-50 text-amber-900'
      : 'border-l-primary bg-accent/30 text-foreground';
  const iconClass =
    tone === 'error'
      ? 'text-red-600'
      : tone === 'warning'
      ? 'text-amber-600'
      : 'text-primary';
  const Icon = tone === 'info' ? RotateCcw : AlertTriangle;
  return (
    <div
      data-testid={testid}
      className={cn(
        'flex items-start gap-2 rounded-r-md border-l-4 p-3 text-[12px] leading-relaxed',
        toneClass,
      )}
    >
      <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', iconClass)} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
};

export default SplitEditor;
